import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlipCameraIosIcon from '@mui/icons-material/FlipCameraIos';

interface CameraCaptureModalProps {
  open: boolean;
  title?: string;
  facing?: 'user' | 'environment';
  onClose: () => void;
  onCapture: (file: File, dataUrl: string) => void;
}

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ open, title = 'Foto machen', facing = 'user', onClose, onCapture }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [ready, setReady] = useState(false);
  const [currentFacing, setCurrentFacing] = useState<'user' | 'environment'>(facing);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  const start = useCallback(async (fm: 'user' | 'environment') => {
    try {
      stop();
      const constraints: MediaStreamConstraints = {
        video: { facingMode: { ideal: fm }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      } as any;
      let stream: MediaStream | null = null;
      try { stream = await navigator.mediaDevices.getUserMedia(constraints); } catch { stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }); }
      if (!stream) throw new Error('No camera');
      streamRef.current = stream;
      if (videoRef.current) {
        const v = videoRef.current;
        v.srcObject = stream;
        (v as any).playsInline = true;
        v.autoplay = true as any;
        v.muted = true;
        const play = async () => { try { await v.play(); setReady(true); } catch {} };
        v.onloadedmetadata = play;
        v.onloadeddata = play as any;
        setTimeout(play, 60);
      }
    } catch (e) {
      setReady(false);
    }
  }, [stop]);

  useEffect(() => {
    if (open) start(currentFacing);
    return () => stop();
  }, [open, currentFacing, start, stop]);

  const handleCapture = async () => {
    const v = videoRef.current; const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 1280; const h = v.videoHeight || 720;
    c.width = w; c.height = h;
    const ctx = c.getContext('2d'); if (!ctx) return;
    ctx.save();
    if (currentFacing === 'user') {
      ctx.translate(w, 0); ctx.scale(-1, 1);
    }
    ctx.drawImage(v, 0, 0, w, h);
    ctx.restore();
    const dataUrl = c.toDataURL('image/jpeg', 0.92);
    const res = await fetch(dataUrl); const blob = await res.blob();
    const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
    onCapture(file, dataUrl);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ position: 'relative', background: '#000' }}>
          <video
            ref={videoRef}
            style={{ width: '100%', height: '60vh', objectFit: 'cover', transform: currentFacing === 'user' ? 'scaleX(-1)' : 'none' }}
            autoPlay playsInline muted
          />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <Button startIcon={<FlipCameraIosIcon />} onClick={() => setCurrentFacing(p => (p === 'user' ? 'environment' : 'user'))} disabled={!open}>
            Kamera wechseln
          </Button>
        </Box>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>Abbrechen</Button>
          <Button variant="contained" onClick={handleCapture} disabled={!ready}>Aufnehmen</Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CameraCaptureModal;
